// Vue Router
import { createRouter, createWebHistory } from 'vue-router';

//Services
import CSessao from '@/services/storage/CSessao';
import CPreferenciasSecurity from '@/services/storage/preferencias/security/CPreferenciasSecurity';

// Utils
import { irParaRotaLogin } from './NavegacaoUtils';

// Enums
import EOrigemTokenJWT from '@/shared/enums/EOrigemTokenJWT';
import ETipoAcessoAplicacao from '@/shared/enums/ETipoAcessoAplicacao';

// Rotas
import { authRoutes } from './rotasAuth';
import { temasRoutes } from './temas/rotasTemas';
import { manutencaoRoutes } from './rotasManutencao';
import { rotasDocumentacao } from './rotasDocumentacao';
import { rotasTemaVendas } from './temas/rotasTemaVendas';
import { adminAuthRoutes, adminRoutes } from './rotasAdmin';
import { rotasTemaFinanceiro } from './temas/rotasTemaFinanceiro';
import { rotasTemaEstoque } from './temas/rotasTemaEstoque';
import { rotasPoliticaPrivacidade } from './rotasPoliticaPrivacidade';

// Constantes
import { cMENUS_DASHBOARDS_DE_TODOS_TEMAS } from '@/shared/constants/ConstantesTemas';

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...rotasDocumentacao,
    ...rotasPoliticaPrivacidade,

    {
      path: '/comercial',
      name: 'PaginaComercial',
      component: () => import('@/modules/comercial/PaginaComercial.vue'),
      meta: { requiresAuth: false },
    },

    {
      path: '/admin',
      component: () => import('@/layouts/default/LayoutDefault.vue'),
      meta: { isRotaAdmin: true },
      children: [
        ...adminAuthRoutes,
        ...manutencaoRoutes,
        {
          path: 'dashboards',
          name: 'DashboardAdmin',
          //component: () => import('@/modules/temas/tema-administrativo/LayoutAdmin.vue'),
          children: [...adminRoutes],
        },
      ],
    },

    {
      path: '/:cliente?',
      component: () => import('@/layouts/default/LayoutDefault.vue'),
      children: [
        ...authRoutes,
        ...manutencaoRoutes,
        {
          // Configuração de redirecionamento de /:cliente -> /:cliente/temas
          path: '',
          redirect: (to) => {
            const cliente = to.params.cliente;
            const path = cliente ? `/${cliente}/temas` : '/temas';
            return path;
          },
        },

        {
          path: 'temas',
          meta: { isRotasTemas: true },
          children: [
            ...temasRoutes,
            {
              path: '',
              component: () => import('@/layouts/dashboard/LayoutDashboard.vue'),
              meta: {
                requiresAuth: true,
                requerPerfil: true,
              },
              children: [
                // Rota coringa para subrotas inválidas de um tema
                {
                  path: '',
                  redirect: (to) => {
                    const { cliente } = to.params;
                    return `/${cliente}/temas`;
                  },
                },

                ...rotasTemaVendas,
                ...rotasTemaFinanceiro,
                ...rotasTemaEstoque,
              ],
            },
          ],
        },
      ],
    },

    {
      path: '/:pathMatch(.*)*',
      component: () => import('@/components/manutencao/Error404Page.vue'),
    },
  ],
});

router.beforeEach(async (to, from) => {
  if (!CSessao.isInicializado) {
    await CSessao.inicializar([CPreferenciasSecurity.storeName, ...cMENUS_DASHBOARDS_DE_TODOS_TEMAS]);
  }

  const params = new URLSearchParams(window.location.search);
  const host = params.get('host') ?? '';
  const porta = params.get('porta') ?? '';
  const clienteQuery = params.get('cliente') ? Number(params.get('cliente')) : null;

  const isRotaAdmin = to.meta.isRotaAdmin ?? false;
  const isRotasTemas = to.meta.isRotasTemas ?? false;
  const clienteRota = to.params.cliente ? Number(to.params.cliente) : null;

  let lCodigoCliente: number | null = null;

  if (clienteQuery && CSessao.servidorAplicacao.codigo !== clienteQuery) {
    lCodigoCliente = clienteQuery;
    CSessao.servidorAplicacao.codigo = clienteQuery;
    await CSessao.definirServidorAplicacao(CSessao.servidorAplicacao);
  } else if (clienteRota && CSessao.servidorAplicacao.codigo !== clienteRota) {
    lCodigoCliente = clienteRota;
    CSessao.servidorAplicacao.codigo = clienteRota;
    await CSessao.definirServidorAplicacao(CSessao.servidorAplicacao);
  }

  if (host || porta) {
    CSessao.hostLocal = host;
    CSessao.portaLocal = porta;

    if (CSessao.sessaoAtual.cliente.codigo !== 0) {
      CSessao.sessaoAtual.tipoAcesso = ETipoAcessoAplicacao.INTERNO;
      await CSessao.definirSessaoAtual(CSessao.sessaoAtual);
    }
  }

  // Se não for uma rota de administração, verifica se o cliente está definido
  if (!to.params.cliente && isRotasTemas) {
    if (CSessao.servidorAplicacao.codigo) {
      // Redireciona para mesma rota, adicionando o cliente no path
      return {
        path: `/${CSessao.servidorAplicacao.codigo}${to.path}`,
        query: to.query,
        hash: to.hash,
      };
    }

    // Se não tem cliente e não tem no cache, aí sim vai para o login
    return irParaRotaLogin(to, from);
  }

  // Verifica se a rota exige autenticação
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Se for uma rota publica, permite o acesso livremente
  if (!requiresAuth) return true;

  // Se não houver token na sessão atual, redireciona para o login genérico
  if (!CSessao.sessaoAtual.token) {
    return irParaRotaLogin(to, from);
  }

  // Valida se a origem do token corresponde ao ambiente acessado (ERP vs Admin)
  if (isRotasTemas && CSessao.sessaoAtual.origem !== EOrigemTokenJWT.ERP) {
    return irParaRotaLogin(to, from);
  }

  if (isRotaAdmin && CSessao.sessaoAtual.origem !== EOrigemTokenJWT.API) {
    return irParaRotaLogin(to, from);
  }

  if (cMENUS_DASHBOARDS_DE_TODOS_TEMAS.includes(String(to.name))) {
    CSessao.definirPreferenciasDataSet(String(to.name));
  }

  // Se passou por todas as validações de sessão e ambiente, libera o acesso
  // Gualter: A validação de permissões de menu/perfil (Forbidden) ocorrerá no nível do componente devido a questões de ordem de montagem/acesso de informações.
  return true;
});
